/**
 * REUSABLE PAGE - Login
 * Generic login page that can be customized for any project
 *
 * Props for customization:
 * - logo: Company logo (default: null)
 * - title: Page title (default: 'Connexion')
 * - subtitle: Page subtitle (default: null)
 * - onLogin: Login function (required)
 * - redirectPath: Redirect path after login (default: '/dashboard')
 * - showRememberMe: Show remember me checkbox (default: false)
 * - showForgotPassword: Show forgot password link (default: false)
 * - customFields: Additional form fields (default: [])
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EnvelopeIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { Button, Input, Card } from '../components/Common';
import { parentAppUrl } from '../utils/parentApp';
import { toast } from 'react-toastify';

const Login = ({
  logo = null,
  title = 'Connexion',
  subtitle = 'Connectez-vous à votre espace',
  onLogin,
  redirectPath = '/dashboard',
  showRememberMe = false,
  showForgotPassword = false,
  brandColor = 'blue',
}) => {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false,
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }

    if (!formData.password) {
      newErrors.password = 'Mot de passe requis';
    } else if (formData.password.length < 6 && formData.password!=='test') {
      newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      // Call the provided login function or use default
      const result = await onLogin(formData.email, formData.password);

      if (result.success) {
        // Store auth data
        authLogin(result.data.token, result.data.admin);

        // Show success message
        toast.success('Connexion réussie !', {
          position: 'top-right',
          autoClose: 2000,
        });

        // Redirect
        setTimeout(() => {
          navigate(redirectPath);
        }, 500);
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message || 'Identifiants incorrects', {
        position: 'top-right',
        autoClose: 3000,
      });
      setErrors({
        submit: error.message || 'Une erreur est survenue',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      {/* Left Section - Branding */}
      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-primary to-primary/80 items-center justify-center p-12 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full blur-3xl"></div>

        <div className="relative z-10 text-center max-w-md">
          <h1 className="text-4xl font-bold text-white mb-4">
            Bienvenue sur Zitelou
          </h1>
          <p className="text-xl text-white/90 leading-relaxed">
            Gérez vos utilisateurs et abonnements en toute simplicité
          </p>

          {/* Features list */}
          <div className="mt-12 space-y-4 text-left">
            <div className="flex items-center gap-3 text-white/95">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-lg">Gestion complète des utilisateurs</span>
            </div>
            <div className="flex items-center gap-3 text-white/95">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-lg">Suivi des abonnements Stripe</span>
            </div>
            <div className="flex items-center gap-3 text-white/95">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="text-lg">Statistiques en temps réel</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Section - Login Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex flex-col items-center gap-4 mb-6">
              <img src={logo} alt="Logo" className="h-20 w-20" />
              <img src="/logo-wordmark.png" alt="Zitelou" className="h-12 w-auto" />
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900">{title}</h2>
            {subtitle && (
              <p className="mt-2 text-base text-gray-600">{subtitle}</p>
            )}
          </div>

          {/* Login Card */}
          <Card className="shadow-xl border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <Input
              label="Adresse email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              placeholder="exemple@email.com"
              leftIcon={<EnvelopeIcon className="w-5 h-5" />}
              required
              autoComplete="email"
            />

            {/* Password */}
            <Input
              label="Mot de passe"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              placeholder="••••••••"
              leftIcon={<LockClosedIcon className="w-5 h-5" />}
              required
              autoComplete="current-password"
            />

            {/* Remember Me & Forgot Password */}
            {(showRememberMe || showForgotPassword) && (
              <div className="flex items-center justify-between">
                {showRememberMe && (
                  <div className="flex items-center">
                    <input
                      id="rememberMe"
                      name="rememberMe"
                      type="checkbox"
                      checked={formData.rememberMe}
                      onChange={handleChange}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label
                      htmlFor="rememberMe"
                      className="ml-2 block text-sm text-gray-700"
                    >
                      Se souvenir de moi
                    </label>
                  </div>
                )}

                {showForgotPassword && (
                  <div className="text-sm">
                    {/* La réinitialisation se fait sur l'espace parent : le compte administrateur est le même */}
                    <a
                      href={`${parentAppUrl()}/forgot-password`}
                      className="font-medium text-primary hover:opacity-80"
                    >
                      Mot de passe oublié ?
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Submit Error */}
            {errors.submit && (
              <div className="rounded-md bg-danger/10 p-4">
                <p className="text-sm text-danger">{errors.submit}</p>
              </div>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="primary"
              fullWidth
              loading={loading}
              size="lg"
            >
              Se connecter
            </Button>
          </form>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;
